import { Component } from '@theme/component';
import { turnstileRequired, turnstileToken as solveTurnstile } from '@theme/turnstile';

/**
 * Contact form → the YUUTH concierge.
 *
 * Posts to the App Proxy at /apps/invite/contact. The Worker stores the message
 * and emails the configured team recipient(s). Guest path; progressive
 * enhancement over a real <form>.
 */
class ContactForm extends Component {
  /** @type {boolean} */
  #busy = false;

  mount() {
    this.on(this, 'submit', (event) => {
      event.preventDefault();
      this.#submit();
    });
    // Reveal the review-time note when the applicant answers "Yes".
    const applied = /** @type {HTMLSelectElement | undefined} */ (this.refs['applied']);
    const note = /** @type {HTMLElement | undefined} */ (this.refs['appliedNote']);
    if (applied && note) {
      const sync = () => { note.hidden = applied.value !== 'yes'; };
      // this.on(), never a raw addEventListener: Component ties every listener
      // registered this way to the AbortController it aborts on disconnect.
      // A raw listener survives disconnect and leaks one handler per Theme
      // Editor section reload — this was the only such call in the theme.
      this.on(applied, 'change', sync);
      sync();
    }
  }

  async #submit() {
    if (this.#busy) return;

    const name = this.#value('name');
    const email = this.#value('email');
    const message = this.#value('message');
    const appliedEl = /** @type {HTMLSelectElement | undefined} */ (this.refs['applied']);
    const applied = appliedEl?.value === 'yes';

    if (!name || !email) {
      this.#status('Please add your name and email address.', 'error');
      return;
    }

    this.#setBusy(true);
    const turnstileToken = await solveTurnstile(this, () =>
      this.#status('Verifying you are human…', 'pending')
    );
    if (turnstileRequired(this) && !turnstileToken) {
      this.#status('We could not verify your browser. Please refresh and try again.', 'error');
      this.#setBusy(false);
      return;
    }
    this.#status('Sending…', 'pending');

    try {
      const response = await fetch('/apps/invite/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, applied, message, turnstileToken }),
      });

      if (response.status === 403) {
        this.#status('We could not verify your browser. Please refresh and try again.', 'error');
        return;
      }
      if (response.status === 400) {
        let m = 'Please check the form and try again.';
        try { const b = await response.json(); if (b && b.message) m = b.message; } catch {}
        this.#status(m, 'error');
        return;
      }
      if (!response.ok) {
        this.#status('Something went wrong. Please try again shortly.', 'error');
        return;
      }

      this.#status('Thank you. Your message has been sent.', 'success');
    } catch {
      this.#status('Could not reach the server. Please try again.', 'error');
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {string} ref @returns {string} */
  #value(ref) {
    const el = /** @type {HTMLInputElement | HTMLTextAreaElement | undefined} */ (this.refs[ref]);
    return el?.value?.trim() ?? '';
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const button = /** @type {HTMLButtonElement | undefined} */ (this.refs['submit']);
    if (button) button.disabled = busy;
  }

  /** @param {string} message @param {'pending' | 'error' | 'success'} tone */
  #status(message, tone) {
    const el = /** @type {HTMLElement | undefined} */ (this.refs['status']);
    if (!el) return;
    el.textContent = message;
    el.dataset.tone = tone;
  }
}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactForm);
}
